import { useRef, type Dispatch, type RefObject } from "react";
import type { Order, OrderPayment } from "../../lib/types.ts";
import type { Aktion } from "../../state/reduzierer.ts";
import { ApiFehler, KonfliktFehler } from "../../api/basis.ts";
import { meldeFehler, meldeNachladeFehler } from "../../state/fehlerStapel.ts";
import { bestellungLaden, SSE_ENTITAET as BESTELLUNG_ENTITAET } from "../../api/bestellungen.ts";
import { rateAnlegen, rateLoeschen, ratenAendern } from "../../api/zahlungsraten.ts";
import { berlinHeute } from "../../lib/berlinTag.ts";
import { BESTELLUNG_WEG_MELDUNG, mitBestellbezug } from "../../lib/bestellkennung.ts";
import { fehlertext } from "../../lib/fehlertext.ts";
import { useMontiertRef } from "../../state/useMontiertRef.ts";
import type { IstZahlungHandler } from "../Drawer/PaymentTab/IstZahlung.tsx";
import { planeRatenSynchronisierung } from "../../lib/zahlungssynchronisation.ts";
import {
  belegHochladen as istBelegHochladen,
  belegVerknuepfen as istBelegVerknuepfen,
  bezahltMarkieren,
  zahlungZuruecknehmen,
  zahlungsdatumAendern,
} from "../../api/zahlungIst.ts";
import { dateiZuGrossText, istZuGross, NachladenNachUploadFehler, uploadFehlerText } from "../../lib/upload.ts";

/**
 * Die Saetze nach einem 409 oder 404 bei einem Vorgang der Ist-Zahlung (Bezahlt markieren, Datum, Rücknahme, Beleg).
 * R4b (Fehleranzeige Paket 2): es wurde NICHTS gespeichert; erst wird der aktuelle Stand nachgeladen, DANN steht GENAU
 * EINE Meldung da - je nach Ausgang des Nachladens (R-T11d, R-T11f):
 *  - `geladen`: der Stand ist da; nur nach dem Laden ist "ist geladen" eine Aussage ueber etwas;
 *  - `nichtGeladen`: das Laden scheiterte mit einer Ursache, die der Nutzer lesen kann; sie steht dahinter;
 *  - `neutral`: das Laden endete ohne meldbaren Text (Abbruch, 401): nur das, was feststeht, ohne Aussage ueber den Stand.
 * Liefert das Nachladen `null` (die Bestellung gibt es nicht mehr), gilt unabhaengig vom Vorgang `BESTELLUNG_WEG_MELDUNG`.
 */
interface KonfliktSaetze {
  readonly geladen: string;
  readonly nichtGeladen: string;
  readonly neutral: string;
}
const ZAHLUNG_KONFLIKT_SAETZE: KonfliktSaetze = {
  geladen:
    "Die Zahlung oder Rate wurde zwischenzeitlich geändert. Der aktuelle Stand ist geladen – bitte prüfen und erneut versuchen.",
  nichtGeladen:
    "Die Zahlung oder Rate wurde zwischenzeitlich geändert. Der aktuelle Stand konnte nicht geladen werden – bitte neu laden.",
  neutral: "Die Zahlung oder Rate wurde zwischenzeitlich geändert.",
};
const ZAHLUNG_WEG_SAETZE: KonfliktSaetze = {
  geladen: "Die Zahlung gibt es nicht mehr. Der aktuelle Stand ist geladen.",
  nichtGeladen: "Die Zahlung gibt es nicht mehr. Der aktuelle Stand konnte nicht geladen werden – bitte neu laden.",
  neutral: "Die Zahlung gibt es nicht mehr.",
};

/** Wie das Nachladen der Bestellung nach einem Ratenschreibzugriff ausging (siehe `ratenNachladen`). */
type RatenNachladeAusgang = "geladen" | "weg" | "gescheitert";

/** So nennt die Oberflaeche den Beleg einer Ist-Zahlung (`IstZahlung.tsx`, Rueckfrage und Download-Meldung). */
const BELEG_BEZEICHNUNG = "Zahlungsbeleg";

/**
 * Der Beleg-Upload einer Ist-Zahlung: die Datei und der Bezug, den jeder stehende Hinweis dazu traegt
 * (`Zahlungsbeleg für BE-10440`). Der Bezug wird beim START gebildet - der Hinweis steht getrennt von der Stelle, an der
 * der Nutzer hochlud, und bis ein Upload von 100 MB scheitert, zeigt der Drawer womoeglich eine andere Bestellung.
 */
interface BelegUpload {
  datei: File;
  bezug: string;
}

/**
 * Synchronisiert Drawer-Raten und schreibt Ist-Zahlungen/Belege; der Plan-Schreibkanal bleibt beim Aufrufer.
 * Jede Meldung dieses Hooks ist ein Fehler und geht seit der Fehleranzeige (Paket 1) in den Fehlerstapel;
 * einen Toast gibt es hier nicht. EINE Ausnahme ist der Beleg-Upload (siehe `zeigeHinweis`).
 *
 * `zeigeHinweis` (Task 7 "Uploads bis 100 MB"): der STEHENDE Hinweis der App (`StehendeHinweise.tsx`). Nur der
 * Beleg-Upload der Ist-Zahlung meldet darueber - die Vorabpruefung, jeder Fehlschlag ausser Konflikt und 404, und ein
 * Fehlschlag allein des Nachladens danach - und NICHT zusaetzlich im Fehlerstapel (keine Doppelmeldung). Alle anderen
 * Schreibvorgaenge gehen in den Fehlerstapel.
 *
 * `bestellKennungVon` (Fix-Runde 1 zu Task 7): die lesbare Kennung der Bestellung zu einer Id
 * (Bestellnummer, sonst die Id) - der Bezug der Beleg-Hinweise, `Zahlungsbeleg für BE-10440: <Grund>`.
 *
 * `drawerId` und `bestellungen` (Fehleranzeige Paket 2, Bestellbezug): die Bestellung, die der Drawer gerade offen hat
 * (`null` = geschlossen), und der Bestand. Eine Meldung im Fehlerstapel, die erst nach einem Drawer-Wechsel oder
 * Schliessen erscheint, nennt die Bestellung (`kontextFuer`); die Beleg-Hinweise haben ihren eigenen Bezug und bleiben.
 */
export function useDrawerZahlungen(
  versende: Dispatch<Aktion>,
  zahlungsbearbeitungNachladenNoetigRef: RefObject<Set<string>>,
  NACHLADEN_FEHLGESCHLAGEN_MELDUNG: string,
  zeigeHinweis: (text: string) => void,
  bestellKennungVon: (bestellungId: string) => string,
  drawerId: string | null,
  bestellungen: readonly Order[],
) {
  // R3 (Fehleranzeige Paket 1, Bestellbezug Paket 2): ERFOLGE einer nicht mehr offenen Bestellung duerfen verworfen
  // werden, FEHLER nie - dann nennt der Kontext die Bestellung. Dasselbe Muster wie `useDrawerTasks`
  // (`aktuellerVorgang`/`kontextFuer`): ein A -> B -> A-Wechsel invalidiert den alten A-Vorgang ebenfalls, und
  // `bestellungenRef` haelt auch nach dem Unmount den letzten Stand der Bestellungen.
  const bestellungenRef = useRef(bestellungen);
  bestellungenRef.current = bestellungen;
  const istEingehaengt = useMontiertRef("passiv");
  const drawerKontext = useRef({ id: drawerId, folge: 0 });
  if (drawerKontext.current.id !== drawerId) {
    drawerKontext.current = { id: drawerId, folge: drawerKontext.current.folge + 1 };
  }

  function aktuellerVorgang(): () => boolean {
    const folge = drawerKontext.current.folge;
    return () => istEingehaengt.current && drawerKontext.current.folge === folge;
  }

  function kontextFuer(id: string, satz: string, istAktuell: () => boolean): string {
    if (istAktuell()) return satz;
    return mitBestellbezug(satz, bestellungenRef.current.find((b) => b.id === id), id);
  }

  /**
   * Ladet die Bestellung nach einem Ratenschreibzugriff neu und ersetzt sie im Store - `payments[]`
   * haengt flach an der Bestellung (wie `tasks[]`), eine einzelne Rate hat im Store keinen eigenen
   * Platz. Gleiches Muster wie `aufgabenNachladen` in `useDrawerTasks.ts`: kein Fehler fliegt zum Aufrufer, der Ausgang
   * kommt als Wert zurueck.
   *
   * FAENGT DEN NACHLADEFEHLER SELBST AB und LOEST NIE ab (Review-Befund, Important): schlagen die
   * Schreibzugriffe in `drawerRatenSynchronisieren` erfolgreich durch und scheitert erst das
   * anschliessende `GET /api/orders/:id` (Netz, 401, 500), soll der Nutzer `NACHLADEN_FEHLGESCHLAGEN_
   * MELDUNG` sehen - NICHT die generische Sammelmeldung von `drawerRatenSynchronisieren`s eigenem
   * `.catch`, die faelschlich "nicht geaendert" suggeriert, obwohl gespeichert wurde (ein erneuter
   * Klick legte den bereits gespeicherten Ratensatz sonst ein zweites Mal an). Genau das Muster von
   * `aufgabenNachladen` (`useDrawerTasks.ts`): die eigene Promise loest nach einem Fehlschlag trotzdem auf, statt die
   * Ablehnung an den Aufrufer weiterzureichen.
   *
   * Der Rueckgabewert sagt, wie es ausging (er loest NIE ab):
   *  - `geladen`: der Stand ist im Store;
   *  - `weg`: das Laden lieferte `null`, die Bestellung gibt es nicht mehr (404). Es wurde nichts eingespielt, und der
   *    Drawer zeigt den alten Stand weiter: wer eine Aussage ueber den Stand macht, darf das hier nicht als "geladen"
   *    behandeln (R-T11d). Eine Meldung ist dafuer nicht abgesetzt;
   *  - `gescheitert`: das Laden scheiterte; die Meldung ist schon abgesetzt bzw. dem Aufrufer ueberlassen (`nachladeFehler`).
   *
   * `nachladeFehler` sagt, wie ein Fehlschlag des Nachladens gemeldet wird:
   *  - eine Funktion: wer fuer einen gescheiterten Nachladeschritt eine eigene Meldung hat, uebernimmt sie hier -
   *    der Beleg-Upload der Ist-Zahlung (`NachladenNachUploadFehler`, siehe `istZahlungSchreiben`; dann entfaellt der
   *    Eintrag im Fehlerstapel, keine Doppelmeldung) und der Konflikt (`konfliktNachladen`, R4b: dort wurde NICHTS
   *    gespeichert, die Meldung wartet auf den Ausgang des Nachladens und steht erst danach da);
   *  - ohne Angabe bleibt es beim R4-Weg (`meldeNachladeFehler`).
   *
   * `istAktuell` (R3, siehe `kontextFuer`): ist der Drawer inzwischen weitergezogen, nennt der R4-Satz dieser Funktion die
   * Bestellung. Die eigene Meldung des Aufrufers (Funktion) bleibt, wie sie ist.
   */
  function ratenNachladen(
    id: string,
    istAktuell: () => boolean,
    nachladeFehler?: (ursache: unknown) => void,
  ): Promise<RatenNachladeAusgang> {
    return bestellungLaden(id)
      .then((neu): RatenNachladeAusgang => {
        if (neu === null) return "weg";
        versende({ art: "nachgeladen", entitaet: BESTELLUNG_ENTITAET, id, datensatz: neu });
        return "geladen";
      })
      .catch((fehler: unknown): RatenNachladeAusgang => {
        console.error(`Bestellung "${id}" konnte nach der Ratenaenderung nicht geladen werden:`, fehler);
        if (nachladeFehler) {
          nachladeFehler(fehler);
          return "gescheitert";
        }
        // R4: geschrieben ist geschrieben; der Eintrag sagt, dass nur die Anzeige veraltet ist, und warum - ausser die
        // Ursache raet zum Wiederholen (R-Z2: ein "erneut versuchen" hinter dem Erfolgssatz legte die Rate doppelt an).
        meldeNachladeFehler(fehler, kontextFuer(id, NACHLADEN_FEHLGESCHLAGEN_MELDUNG, istAktuell));
        return "gescheitert";
      });
  }

  /**
   * R4b: nach einem 409/404 ist NICHTS gespeichert. Erst wird nachgeladen, DANN kommt GENAU EINE Meldung - nie zuerst
   * eine, die "Der aktuelle Stand ist geladen" behauptet, und danach eine zweite, die das Gegenteil sagt. Welche, haengt
   * am Ausgang des Nachladens (`KonfliktSaetze`, R-T11d/R-T11f):
   *  - `geladen`: `saetze.geladen`;
   *  - `weg` (`null`: die Bestellung gibt es nicht mehr): `BESTELLUNG_WEG_MELDUNG`, nie "Der aktuelle Stand ist geladen";
   *  - `gescheitert` mit meldbarer Ursache: `saetze.nichtGeladen` mit der Ursache dahinter (`meldeFehler`; ein
   *    Wiederholungsrat ist hier harmlos, es wurde nichts geschrieben);
   *  - `gescheitert` ohne meldbaren Text (Abbruch, 401): `saetze.neutral`. Die gescheiterte AKTION meldet R4b immer; nur
   *    der Nachsatz zum Stand haengt am Nachladen (R-T10c).
   * Alle Saetze nennen die Bestellung, wenn der Drawer inzwischen weitergezogen ist - bewertet wird das erst NACH dem
   * Nachladen.
   */
  function konfliktNachladen(id: string, istAktuell: () => boolean, saetze: KonfliktSaetze): Promise<void> {
    let nachladeUrsache: unknown;
    return ratenNachladen(id, istAktuell, (ursache) => {
      nachladeUrsache = ursache;
    }).then((ausgang) => {
      switch (ausgang) {
        case "geladen":
          meldeFehler(kontextFuer(id, saetze.geladen, istAktuell));
          return;
        case "weg":
          meldeFehler(kontextFuer(id, BESTELLUNG_WEG_MELDUNG, istAktuell));
          return;
        case "gescheitert": {
          const grund = fehlertext(nachladeUrsache);
          if (grund === null) meldeFehler(kontextFuer(id, saetze.neutral, istAktuell));
          else meldeFehler(grund, kontextFuer(id, saetze.nichtGeladen, istAktuell));
          return;
        }
      }
    });
  }

  /**
   * Gleicht den alten gegen den gewuenschten neuen Ratensatz ab (`planeRatenSynchronisierung`) und
   * fuehrt die noetigen POST/PATCH/DELETE-Aufrufe NACHEINANDER aus (nicht parallel: eine Bestellung mit
   * zwei Raten, die von split auf total wechselt, loescht zwei Zeilen und legt eine neue an - parallel
   * liefe das Risiko einer Zwischenlage mit drei oder null Zeilen, wenn ein Aufruf schneller ist als
   * ein anderer). Schlaegt einer der Aufrufe fehl (z. B. VersionConflictError), bricht die Kette ab;
   * bereits ausgefuehrte Teiloperationen bleiben stehen - dieselbe Charakteristik wie jeder andere
   * Mehrfach-Schreibzugriff in diesem Modul (z. B. `drawerAufgabeAnlegen`).
   *
   * `onAendern` bekommt hier NIE `payments` oder `paymentMode` zu sehen - RIEGEL 2
   * (`api/bestellungen.ts#FeldOhneGruppeFehler`) bleibt dadurch unveraendert scharf.
   */
  function drawerRatenSynchronisieren(
    id: string,
    alte: readonly OrderPayment[],
    neue: readonly OrderPayment[],
  ): void {
    const istAktuell = aktuellerVorgang();
    const plan = planeRatenSynchronisierung(alte, neue);
    const kette: Array<() => Promise<unknown>> = [
      ...plan.anzulegen.map((eintrag) => () => rateAnlegen(id, eintrag.felder)),
      ...plan.zuAendern.map(
        (eintrag) => () => ratenAendern(id, eintrag.payId, eintrag.felder, eintrag.rowVersion),
      ),
      ...plan.zuLoeschen.map((eintrag) => () => rateLoeschen(id, eintrag.payId, eintrag.rowVersion)),
    ];
    // NACHEINANDER statt `Promise.all`, siehe Kommentar oben - ein simples `for…of` mit `await`
    // ergibt dieselbe Sequenzialitaet wie eine `reduce`-Kette, ohne deren Typinferenz-Faelle bei
    // gemischten Rueckgabetypen (`Promise<OrderPayment>` vs. `Promise<void>`).
    (async () => {
      for (const schritt of kette) {
        await schritt();
      }
    })()
      .then(() => ratenNachladen(id, istAktuell))
      .catch((fehler: unknown) => {
        // `ratenNachladen` faengt ihren eigenen Fehlschlag ab und loest NIE ab (siehe deren
        // Kopfkommentar) - dieser `.catch` sieht deshalb ausschliesslich Fehlschlaege AUS DER
        // SCHREIBKETTE (`kette`), nie einen Fehlschlag des Nachladens. Dasselbe Muster wie
        // `drawerAendern`/`drawerAufgabeAendern`: der Fehler PROTOKOLLIERT, die Sammelmeldung kann
        // seinen Grund (z. B. 409 mit Serverstand) nicht tragen.
        console.error(`Zahlungsraten der Bestellung "${id}" konnten nicht synchronisiert werden:`, fehler);
        meldeFehler(fehler, kontextFuer(id, "Die Zahlungsraten konnten nicht geändert werden.", istAktuell));
      });
  }

  /**
   * Ein Schreibvorgang der Ist-Zahlung (Bezahlt markieren, Datum, Zuruecknehmen, Belege; Plan 2026-10-04):
   * danach IMMER `ratenNachladen` (Raten, `actual` und der Kopf kommen mit der Bestellung). 409 (Rate oder
   * Zahlung hat sich geaendert, die Antwort traegt `current`) und 404 (Zahlung unbekannt oder fremd) laden zuerst
   * nach und melden DANN genau einmal (R4b, `konfliktNachladen`): "Der aktuelle Stand ist geladen ...", "... konnte
   * nicht geladen werden ..." mit der Ursache, "Die Bestellung gibt es nicht mehr ..." (das Nachladen lieferte `null`) oder
   * der neutrale Satz ohne Aussage ueber den Stand (das Nachladen endete ohne meldbaren Text).
   *
   * `ausgang` sagt, wie ein Fehlschlag gemeldet wird, und schliesst beides gegeneinander aus:
   *  - ein Text (`string`): der Kontext hinter dem Servertext im Fehlerstapel, wenn der Server keinen eigenen Satz
   *    mitgibt (Bezahlt markieren, Datum, Rücknahme, Verknuepfen);
   *  - ein `BelegUpload`: das Hochladen eines Belegs (Task 7 "Uploads bis 100 MB"). Konflikt und 404 bleiben, wie sie
   *    sind (Fehlerstapel). Jeder andere Fehlschlag des Uploads geht als STEHENDER Hinweis hinaus (`uploadFehlerText`:
   *    zu gross, Verbindung, Zeitlimit ...) - ein Upload von 100 MB, der nach Minuten scheitert, darf nicht nach 2,2
   *    Sekunden verschwinden - und traegt den Bezug (`Zahlungsbeleg für BE-10440: ...`). Er geht NICHT zusaetzlich in
   *    den Fehlerstapel. Ist der Beleg gespeichert und scheitert nur das Nachladen danach, ist es ein
   *    `NachladenNachUploadFehler`: der Hinweis sagt "gespeichert, die Anzeige ist aber nicht mehr aktuell" statt
   *    "konnte nicht gespeichert werden" (sonst luede der Nutzer die Datei ein zweites Mal hoch). Ein eigener Satz fuer
   *    den Upload gibt es nicht: `uploadFehlerText` kennt jeden Fall.
   */
  function istZahlungSchreiben(
    id: string,
    schluessel: string,
    schritt: () => Promise<unknown>,
    ausgang: string | BelegUpload,
  ): void {
    if (zahlungsbearbeitungNachladenNoetigRef.current.has(id)) return;
    // Doppelklick: solange ein Vorgang an derselben Rate/Zahlung laeuft, wird der zweite Klick still verworfen
    // (sonst bekaeme er 409 und zeigte einen irrefuehrenden Konflikt, obwohl der erste gelang).
    const marke = `${id}:${schluessel}`;
    if (istZahlungLaeuft.current.has(marke)) return;
    istZahlungLaeuft.current.add(marke);
    const istAktuell = aktuellerVorgang();
    void schritt()
      .then(
      () =>
        ratenNachladen(
          id,
          istAktuell,
          typeof ausgang === "string"
            ? undefined
            : (ursache) =>
                zeigeHinweis(
                  `${ausgang.bezug}: ${uploadFehlerText(
                    new NachladenNachUploadFehler(
                      `Bestellung "${id}" konnte nach dem Hochladen von "${ausgang.datei.name}" nicht neu geladen werden.`,
                      { cause: ursache },
                    ),
                    ausgang.datei,
                  )}`,
                ),
        ),
      (fehler: unknown) => {
        console.error(`Ist-Zahlung der Bestellung "${id}" konnte nicht geschrieben werden:`, fehler);
        if (fehler instanceof KonfliktFehler) {
          return konfliktNachladen(id, istAktuell, ZAHLUNG_KONFLIKT_SAETZE);
        }
        if (fehler instanceof ApiFehler && fehler.status === 404) {
          return konfliktNachladen(id, istAktuell, ZAHLUNG_WEG_SAETZE);
        }
        if (typeof ausgang !== "string") {
          zeigeHinweis(`${ausgang.bezug}: ${uploadFehlerText(fehler, ausgang.datei)}`);
          return undefined;
        }
        // Servertext (400/403/5xx …) statt rohem `.message`: `fehlertext` waehlt ihn, ein 401 meldet nichts.
        meldeFehler(fehler, kontextFuer(id, ausgang, istAktuell));
        return undefined;
      },
    )
      .finally(() => {
        istZahlungLaeuft.current.delete(marke);
      });
  }

  const istZahlungLaeuft = useRef(new Set<string>());

  const istZahlungHandler: IstZahlungHandler = {
    bezahltMarkieren: (id, rate) =>
      istZahlungSchreiben(
        id,
        `rate:${String(rate.id)}`,
        // `paidOn` stets explizit (Spec): heute in Europe/Berlin, nicht das UTC-Datum des Browsers.
        () => bezahltMarkieren(id, String(rate.id), Number(rate.rowVersion), berlinHeute()),
        "Die Rate konnte nicht als bezahlt markiert werden.",
      ),
    datumAendern: (id, zahlung, paidOn) =>
      istZahlungSchreiben(
        id,
        `zahlung:${zahlung.id}`,
        () => zahlungsdatumAendern(id, zahlung.id, paidOn),
        "Das Zahlungsdatum konnte nicht geändert werden.",
      ),
    zuruecknehmen: (id, rate, zahlung) =>
      istZahlungSchreiben(
        id,
        `rate:${String(rate.id)}`,
        () => zahlungZuruecknehmen(id, zahlung.id, Number(rate.rowVersion)),
        "Die Zahlung konnte nicht zurückgenommen werden.",
      ),
    belegHochladen: (id, zahlung, datei) => {
      // Der Bezug entsteht beim START (siehe `BelegUpload`) und gilt fuer jeden Hinweis dieses Belegs.
      const bezug = `${BELEG_BEZEICHNUNG} für ${bestellKennungVon(id)}`;
      // Global Constraint 17 - die Pruefung erspart den serverseitigen Fehlversuch (413). Als stehender Hinweis
      // (Task 7), nicht als Toast.
      if (istZuGross(datei)) {
        zeigeHinweis(`${bezug}: ${dateiZuGrossText(datei)}`);
        return;
      }
      istZahlungSchreiben(id, `zahlung:${zahlung.id}`, () => istBelegHochladen(id, zahlung.id, datei), { datei, bezug });
    },
    belegVerknuepfen: (id, zahlung, documentId) =>
      istZahlungSchreiben(
        id,
        `zahlung:${zahlung.id}`,
        () => istBelegVerknuepfen(id, zahlung.id, documentId),
        "Der Beleg konnte nicht verknüpft werden.",
      ),
  };

  return [drawerRatenSynchronisieren, istZahlungHandler] as const;
}
